import React, { useEffect, useRef, useState } from "react";
import { useLeadsStore } from "../../../../Stores/LeadAndCustomer/leadsStore";
import { Lead, LeadSource, WorkspaceType } from "shared-types";
import { LeadInteractionDetails } from "./leadInteractionDetails";
import { Button } from "../../../../Common/Components/BaseComponents/Button";
import { PageContainer } from "../../../../Common/Components/BaseComponents/PageContainer";
import { NewLeadModalContent } from "../Leads/NewLeadModalContent";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
    Building2,
    Calendar,
    ChevronDown,
    ChevronUp,
    FileText,
    Mail,
    Phone,
    Plus,
    ScrollText,
    Upload,
    X,
} from "lucide-react";
import { ShowAlertWarn } from "../../../../Common/Components/BaseComponents/showAlertWarn";
import { showAlert } from "../../../../Common/Components/BaseComponents/ShowAlert";
import { ExportToExcel } from "../exportToExcel";

type SortField = "name" | "status" | "createdAt" | "updatedAt" | "lastInteraction";
type AlertCriterion = "noRecentInteraction" | "statusIsNew" | "oldLead";

// Helper functions for translations
const getLeadSourceLabel = (source: LeadSource) => {
    const sourceLabels: Record<LeadSource, string> = {
        [LeadSource.WEBSITE]: 'אתר אינטרנט',
        [LeadSource.REFERRAL]: 'הפניה',
        [LeadSource.SOCIAL_MEDIA]: 'רשתות חברתיות',
        [LeadSource.EVENT]: 'אירוע',
        [LeadSource.PHONE]: 'טלפון',
        [LeadSource.WALK_IN]: 'הגעה פיזית',
        [LeadSource.EMAIL]: 'אימייל',
        [LeadSource.OTHER]: 'אחר',
    };
    return sourceLabels[source] || source;
};

const getWorkspaceTypeLabel = (type: WorkspaceType) => {
    const typeLabels: Record<WorkspaceType, string> = {
        [WorkspaceType.PRIVATE_ROOM1]: 'חדר פרטי',
        [WorkspaceType.PRIVATE_ROOM2]: 'חדר של שתיים',
        [WorkspaceType.PRIVATE_ROOM3]: 'חדר של שלוש',
        [WorkspaceType.DESK_IN_ROOM]: 'שולחן בחדר',
        [WorkspaceType.OPEN_SPACE]: 'אופן ספייס',
        [WorkspaceType.KLIKAH_CARD]: 'כרטיס קליקה',
        [WorkspaceType.KLIKAH_CARD_UPGRADED]: 'כרטיס קליקה פרו',
        [WorkspaceType.DOOR_PASS]: 'כרטיס כניסה',
        [WorkspaceType.WALL]: 'קיר',
        [WorkspaceType.COMPUTER_STAND]: 'עמדת מחשב',
        [WorkspaceType.RECEPTION_DESK]: 'דלפק קבלה',
        [WorkspaceType.BASE]: 'בסיס',
    };
    return typeLabels[type] || type;
};

const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
        'NEW': 'חדש',
        'CONTACTED': 'נוצר קשר',
        'INTERESTED': 'מעוניין',
        'SCHEDULED_TOUR': 'נקבע סיור',
        'PROPOSAL_SENT': 'נשלחה הצעה',
        'CONVERTED': 'הומר ללקוח',
        'NOT_INTERESTED': 'לא מעוניין',
        'LOST': 'אבד'
    };
    return statusLabels[status?.toUpperCase()] || status;
};
export const LeadInteractions = () => {
    const [sortField, setSortField] = useState<SortField>("name");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [alertCriterion, setAlertCriterion] = useState<AlertCriterion>("noRecentInteraction");
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const allLeadsRef = useRef<Lead[]>([]);
    const [status, setStatus] = useState("");//הוספה
    const [isNewLeadModalOpen, setIsNewLeadModalOpen] = useState(false);
    const navigate = useNavigate();
    const {
        leads,
        fetchLeads,
        handleDeleteLead,
        handleSelectLead,
        resetSelectedLead,
        selectedLead,
    } = useLeadsStore();

    const handleRegistration = (lead: Lead | undefined) => {
        if (lead) {
            if (lead.status?.toLowerCase() === "converted") {
                // Navigate to customers page
                navigate("/leadAndCustomer/customers");
            } else {
                // Navigate to registration form for non-converted leads
                navigate("interestedCustomerRegistration", {state: {data: lead}});
            }
        }
    };
    useEffect(() => {
        fetchLeads().then(() => {
            allLeadsRef.current = useLeadsStore.getState().leads;
        });
    }, [page, fetchLeads]);
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 && !isSearching) {
                setPage((prev) => prev + 1);
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isSearching]);

    const deleteCurrentLead = async (leadId: string) => {
        const confirmed = await ShowAlertWarn(
            "האם אתה בטוח שברצונך למחוק את המתעניין לצמיתות?",
            "לא ניתן לשחזר את המידע לאחר מחיקה.",
            "warning"
        );
        if (confirmed) {
            await handleDeleteLead(leadId);
            showAlert("מחיקה", "לקוח נמחק בהצלחה", "success");
            const latestError = useLeadsStore.getState().error;
            if (latestError) {
                const errorMessage = latestError || "שגיאה בלתי צפויה";
                console.error("Error:", errorMessage);
                showAlert("שגיאה במחיקת מתעניין", errorMessage, "error");
            }
        }
    };
    const handleSearch = (term: string, status: string = "") => {
        setSearchTerm(term);
        setStatus(status);
        setPage(1);
        // אין טקסט ואין סטטוס => החזר הכל
        if (!term.trim() && !status.trim()) {
            setIsSearching(false);
            useLeadsStore.setState({leads: allLeadsRef.current});
            return;
        }
        // :white_check_mark: אם כל הלידים טעונים (לא פונים לשרת בכלל)
        if (allLeadsRef.current.length > 0) {
            const filtered = allLeadsRef.current.filter((l) => {
                const matchesTerm =
                    !term.trim() ||
                    l.name?.toLowerCase().includes(term.toLowerCase()) ||
                    l.phone?.includes(term) ||
                    l.email?.toLowerCase().includes(term.toLowerCase());
                const matchesStatus = !status.trim() ||
                    l.status?.toLowerCase().trim() === status.toLowerCase().trim();
                return matchesTerm && matchesStatus;
            });
            setIsSearching(true);
            useLeadsStore.setState({leads: filtered});
            return;
        }
        // :white_check_mark: אם אין את כל הלידים טעונים => fallback לשרת
        fetch(`${process.env.REACT_APP_API_URL}leads/search?q=${term}&status=${status}`)
            .then((res) => res.json())
            .then((data: Lead[]) => {
                setIsSearching(true);
                useLeadsStore.setState({leads: data.length > 0 ? data : []});
            })
            .catch((err) => {
                console.error("שגיאה בחיפוש מהשרת:", err);
                useLeadsStore.setState({leads: []});
            });
    };
    const isAlert = (lead: Lead): boolean => {
        switch (alertCriterion) {
            case "noRecentInteraction":
                const recentThreshold = new Date(Date.now() - 100 * 24 * 60 * 60 * 1000);
                return !lead.interactions?.some((i) =>
                    new Date(i.updatedAt || i.createdAt || i.date) >= recentThreshold
                );
            case "statusIsNew":
                return lead.status?.toLowerCase() === "new";
            case "oldLead":
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                return new Date(lead.createdAt || 0) < sixMonthsAgo;
            default:
                return false;
        }
    };
    const getSortValue = (lead: Lead): string | number | Date => {
        switch (sortField) {
            case "name":
                return lead.name?.toLowerCase() || "";
            case "status":
                return lead.status?.toLowerCase() || "";
            case "createdAt":
                return new Date(lead.createdAt || 0);
            case "updatedAt":
                return new Date(lead.updatedAt || 0);
            case "lastInteraction":
                const dates = lead.interactions?.map((i) => new Date(i.updatedAt || i.createdAt)) || [];
                return dates.length > 0 ? Math.max(...dates.map((d) => d.getTime())) : 0;
            default:
                return "";
        }
    };
    const sortedLeads = [...leads].sort((a, b) => {
        const aVal = getSortValue(a);
        const bVal = getSortValue(b);
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    return (
        <PageContainer title="מתעניינים">

            {/* Action Buttons Row */}
            <div className="flex flex-wrap items-center justify-start gap-4 mb-4 mt-4">
                <Button
                    onClick={() => setIsNewLeadModalOpen(true)}
                    color="green"
                    size="sm"
                    icon={<Plus size={14}/>}
                >
                    מתעניין חדש
                </Button>
                <Button
                    onClick={() => navigate("/leadAndCustomer/leads/UploadLeadsFile")}
                    color="gray"
                    size="sm"
                    icon={<Upload size={14}/>}
                >
                    יבוא אקסל
                </Button>
                <ExportToExcel data={ sortedLeads } fileName="לקוחות"/>
            </div>

            {sortedLeads.length > 0 && (
                <>
                    {/* Search, Filter and Sort - Single Row */}
                    <div className="bg-gray-100 p-4 rounded-lg mb-4 border border-gray-200">
                        <div className="flex flex-wrap items-center justify-start gap-4">
                            {/* Search Input */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">חיפוש:</span>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearch(e.target.value, status);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSearch(searchTerm, status);
                                        }
                                    }}
                                    placeholder="שם, טלפון או אימייל"
                                    className="h-9 w-64 border border-gray-300 rounded-lg bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">סטטוס:</span>
                                <select
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value);
                                        handleSearch(searchTerm, e.target.value);
                                    }}
                                    className="h-9 border border-gray-300 rounded-lg bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                >
                                    <option value="">הכל</option>
                                    <option value="NEW">חדש</option>
                                    <option value="CONTACTED">נוצר קשר</option>
                                    <option value="INTERESTED">מעוניין</option>
                                    <option value="SCHEDULED_TOUR">נקבע סיור</option>
                                    <option value="PROPOSAL_SENT">נשלחה הצעה</option>
                                    <option value="CONVERTED">הומר ללקוח</option>
                                    <option value="NOT_INTERESTED">לא מעוניין</option>
                                    <option value="LOST">אבד</option>
                                </select>
                            </div>

                            {/* Sort Controls */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">מיין לפי:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value as SortField)}
                                    className="h-9 border border-gray-300 rounded-lg bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                >
                                    <option value="name">שם</option>
                                    <option value="status">סטטוס</option>
                                    <option value="createdAt">תאריך יצירה</option>
                                    <option value="updatedAt">תאריך עדכון</option>
                                    <option value="lastInteraction">אינטראקציה אחרונה</option>
                                </select>
                            </div>

                            <Button
                                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                                variant="outline"
                                size="sm"
                                icon={sortOrder === "asc" ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                            >
                                {sortOrder === "asc" ? "עולה" : "יורד"}
                            </Button>

                            {/* Clear Filters */}
                            {(searchTerm || status) && (
                                <Button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatus('');
                                        handleSearch('', '');
                                    }}
                                    color="gray"
                                    size="sm"
                                    icon={<X size={14}/>}
                                    className="bg-gray-400 hover:bg-gray-500 text-white"
                                >
                                    נקה
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* הודעת מידע על מערכת ההתרעות */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">i</span>
                            </div>
                            <span className="text-blue-700">מתעניינים אדומים:</span>
                            <select
                                value={alertCriterion}
                                onChange={(e) => setAlertCriterion(e.target.value as AlertCriterion)}
                                className="h-8 border border-blue-300 rounded-md bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            >
                                <option value="noRecentInteraction">לא היו להם אינטראקציות ב-100 הימים האחרונים</option>
                                <option value="statusIsNew">הם חדשים</option>
                                <option value="oldLead">נוצרו לפני יותר מ-6 חודשים</option>
                            </select>
                        </div>
                    </div>
                </>
            )}

            {sortedLeads.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <div className="text-gray-500 text-lg mb-4">אין מתעניינים להציג</div>
                </div>
            ) : (
                sortedLeads.map((lead) => (
                    <LeadCard
                        key={lead.id}
                        lead={lead}
                        isSelected={selectedLead?.id === lead.id}
                        isAlert={isAlert(lead)}
                        onClick={() => {
                            if (selectedLead?.id === lead.id) resetSelectedLead();
                            else handleSelectLead(lead.id!);
                        }}
                        onDelete={() => deleteCurrentLead(lead.id!)}
                        onRegister={() => handleRegistration(lead)}
                        children={
                            selectedLead?.id === lead.id && <LeadInteractionDetails/>
                        }
                    />
                ))
            )}

            {/* New Lead Modal */}
            {isNewLeadModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
                        {/* Close Button */}
                        <button
                            onClick={() => setIsNewLeadModalOpen(false)}
                            className="absolute top-4 left-4 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full w-9 h-9 flex items-center justify-center shadow transition z-10"
                            aria-label="סגור"
                        >
                            <X size={18}/>
                        </button>

                        {/* Form Content */}
                        <div className="p-6">
                            <NewLeadModalContent onClose={() => setIsNewLeadModalOpen(false)}/>
                        </div>
                    </div>
                </div>
            )}
        </PageContainer>
    );
};
// LeadCard Component (עיצוב בלבד)
const LeadCard = ({
                      lead,
                      isSelected,
                      isAlert,
                      onClick,
                      onDelete,
                      onRegister,
                      children,
                  }: {
    lead: Lead;
    isSelected: boolean;
    isAlert: boolean;
    onClick: () => void;
    onDelete: () => void;
    onRegister: () => void;
    children?: React.ReactNode;
}) => {
    const [open, setOpen] = useState(false);
    const initials = lead.name?.charAt(0).toUpperCase() || "?";
    return (
        <div
            className={`rounded-xl border shadow p-5 mb-4 bg-white transition-all duration-300 cursor-pointer ${isSelected ? "bg-blue-100 border-blue-300" : isAlert ? "border-red-500 bg-red-50" : ""
            }`}
            onClick={() => {
                setOpen(!open);
                onClick();
            }}
        >
            <div className="flex justify-between items-start">
                <div className="flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-4">
                        <div
                            className="w-10 h-10 flex items-center justify-center rounded-full font-bold bg-blue-200 text-blue-800">
                            {initials}
                        </div>
                        <div className="text-xl font-bold text-gray-900">{lead.name}</div>
                        <div className="text-sm text-gray-500">{getStatusLabel(lead.status)}</div>
                    </div>
                    <div className="flex items-center gap-2 text-lg font-semibold text-blue-600 mr-14">
                        <Phone size={18}/> {lead.phone}
                    </div>
                </div>
                {open ? <ChevronUp/> : <ChevronDown/>}
            </div>
            {open && (
                <div className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                        <div className="space-y-2">
                            <div className="flex gap-2 items-center"><Mail size={16}/> {lead.email}</div>
                            <div className="flex gap-2 items-center"><Building2 size={16}/> סוג עסק: {lead.businessType}
                            </div>
                            <div className="flex gap-2 items-center"><ScrollText size={16}/> מתעניין
                                ב: {getWorkspaceTypeLabel(lead.interestedIn)}</div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex gap-2 items-center"><FileText
                                size={16}/> מקור: {getLeadSourceLabel(lead.source)}</div>
                            <div className="flex gap-2 items-center"><Calendar size={16}/> נוצר
                                ב: {new Date(lead.createdAt || 0).toLocaleDateString()}</div>
                        </div>
                    </div>
                    <div className="flex gap-4 mt-2">
                        <Button 
                            color={lead.status?.toLowerCase() === "converted" ? "blue" : "green"} 
                            size="sm" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onRegister();
                            }}
                        >
                            {lead.status?.toLowerCase() === "converted" ? "הצג לקוחות" : "לטופס רישום"}
                        </Button>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            color="red"
                            size="sm"
                            icon={<FontAwesomeIcon icon={faTrash}/>}
                            className="text-red-600 hover:text-red-800 bg-transparent hover:bg-red-50 shadow-none"
                        >
                            מחק
                        </Button>
                    </div>
                    {children}
                </div>
            )}
        </div>
    );
};
